import { createTheme, ThemeOptions } from '@mui/material/styles';

// Sophisticated blue and orange color palette theme configuration
const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#0927eb', // Vivid Blue
      light: '#3A4FED', // Lighter Vivid Blue
      dark: '#0619A8', // Darker Vivid Blue
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff5200', // Vivid Orange
      light: '#FF7A33', // Lighter Vivid Orange
      dark: '#CC4100', // Darker Vivid Orange
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5', // Light grey
      paper: '#ffffff', // Solid white
    },
    text: {
      primary: '#000000', // Black
      secondary: '#4A5568', // Medium gray
    },
    grey: {
      50: '#f8f8f8',
      100: '#eeeeee',
      200: '#E2E8F0',
      300: '#CBD5E0',
      400: '#A0AEC0',
      500: '#718096',
      600: '#4A5568',
      700: '#2D3748',
      800: '#1A202C',
      900: '#000000',
    },
    success: {
      main: '#38A169', // Green
      light: '#68D391',
      dark: '#2F855A',
    },
    warning: {
      main: '#BF4417', // Rust/Burnt Orange
      light: '#E55A2B',
      dark: '#9A2E0A',
    },
    error: {
      main: '#E53E3E', // Red
      light: '#FC8181',
      dark: '#C53030',
    },
    info: {
      main: '#033A81', // Dark Blue/Navy
      light: '#1A4F9A',
      dark: '#022A5F',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          backgroundColor: '#000000',
          color: '#ffffff',
          fontWeight: 'bold',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#333333',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.4)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: '#ff5200',
          color: '#ff5200',
          fontWeight: 'bold',
          textTransform: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            borderWidth: '2px',
            borderColor: '#CC4100',
            backgroundColor: '#ff5200',
            color: '#ffffff',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 15px rgba(255, 82, 0, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
        },
        elevation3: {
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0927eb',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
              borderColor: '#0927eb',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: 'rgba(17, 61, 165, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(17, 61, 165, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(17, 61, 165, 0.16)',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(17, 61, 165, 0.08)',
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#0927eb',
            '& + .MuiSwitch-track': {
              backgroundColor: '#113DA5',
            },
          },
        },
        track: {
          backgroundColor: '#e5e7eb',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: 'rgba(33, 150, 243, 0.2)',
        },
        bar: {
          borderRadius: 4,
          backgroundColor: '#2196F3',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#d1d5db #f3f4f6',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: '#d1d5db',
            minHeight: '24px',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#9ca3af',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            borderRadius: '8px',
            backgroundColor: '#f3f4f6',
          },
        },
      },
    },
  },
};

// Create the theme
export const theme = createTheme(themeOptions);

// Dark theme variant
export const darkTheme = createTheme({
  ...themeOptions,
  palette: {
    ...themeOptions.palette,
    mode: 'dark',
    primary: {
      main: '#3A4FED', // Lighter blue for dark mode
      light: '#6B7CFE', // Even lighter
      dark: '#0927eb', // Original blue
      contrastText: '#ffffff',
    },
    background: {
      default: '#0f1419', // Very dark background
      paper: '#1a1f29', // Slightly lighter for cards
    },
    text: {
      primary: '#f9fafb', // Gray-50
      secondary: '#9ca3af', // Gray-400
    },
  },
  components: {
    ...themeOptions.components,
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1f29',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1f29',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1a1f29',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1a1f29',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: '#3A4FED',
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
              borderColor: '#3A4FED',
            },
          },
          '& .MuiInputBase-input': {
            color: '#f9fafb',
          },
          '& .MuiInputLabel-root': {
            color: '#9ca3af',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        ...themeOptions.components?.MuiButton?.styleOverrides,
        contained: {
          backgroundColor: '#3A4FED',
          color: '#ffffff',
          fontWeight: 'bold',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#0927eb',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(58, 79, 237, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          backgroundColor: 'rgba(58, 79, 237, 0.2)',
          color: '#f9fafb',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'inherit', // Inherit from parent
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1f29',
          color: '#f9fafb',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1f29',
        },
      },
    },
  },
});

export default theme;
